/**
 * Banner 3001 — KUK auxiliary page hero.
 *
 * A background image inside a 1120px wrapper, a white gradient overlay fading
 * left-to-right, and a vertically centred title with an optional icon-link row. Ported
 * from kuk-campus-portal's `AuxiliaryPage` hero; see `Banner3001.tsx` for what
 * v1 hardcodes and what became a field here.
 *
 * The icon slots are v1's fixed `link1`–`link3` / `icon1`–`icon3` pairs, moved
 * onto the primary `metadata` group (this variant has no other dynamic rows,
 * and `banner.metadata` is NOT NULL) and capped at three to match.
 */

export const BANNER_3001_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Header link icons",
  },

  METADATA_LIMITS: {
    metadata: { max: 3 },
  },

  DEFAULT_ROW: {
    id: "1",
    icon: "",
    link: "",
    label: "",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    {
      fieldName: "image",
      label: "Background image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the hero background",
      description:
        "Covers the block (center-cropped). Left empty, the section paints the gradient overlay alone.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "mobile_image",
      label: "Background image (mobile)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Optional — falls back to the main image",
      description: "Used below 768px. The reference has no separate mobile art.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "image_alt",
      label: "Background image alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the image",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title",
      label: "Title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Admission Policy",
      description: "Renders as an h2, as in the reference.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title_color",
      label: "Title color",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "hero_section_height",
      label: "Minimum height (px)",
      value: 300,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "300",
      description:
        "The block grows past this if the title needs the room. Same at every width.",
      required: false,
      defaultValue: 300,
    },
    {
      fieldName: "hero_container_max_width",
      label: "Container max width (px)",
      value: 1120,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1120",
      description: "The centred wrapper the hero sits in.",
      required: false,
      defaultValue: 1120,
    },
    {
      fieldName: "gradient_color_1",
      label: "Gradient start",
      value: "rgba(255, 255, 255, 0.92)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.92)",
      description:
        "Left end of the overlay, behind the title. RGBA, not COLOR: the plain colour picker is 6-digit hex with no alpha, and this fade is defined by its opacity.",
      required: false,
      defaultValue: "rgba(255, 255, 255, 0.92)",
    },
    {
      fieldName: "gradient_color_2",
      label: "Gradient end",
      value: "rgba(255, 255, 255, 0.00)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.00)",
      description:
        "Right end of the overlay, over the image. Opacity 0 lets the image through.",
      required: false,
      defaultValue: "rgba(255, 255, 255, 0.00)",
    },
    {
      fieldName: "icon_color",
      label: "Link icon color",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      description: "Applies to icon-key icons; uploaded images keep their own colors.",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Icon",
      fieldName: "icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload icon",
      defaultValue: "",
    },
    {
      header: "Link",
      fieldName: "link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://…",
      description: "Required — a row with no link is not rendered, as in the reference.",
      defaultValue: "",
    },
    {
      header: "Link label",
      fieldName: "label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Download the policy",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Show this icon",
      defaultValue: true,
    },
  ],
};
